import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { inr, fileUrl, isOnSale, discountPct } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Stars } from "@/components/Reviews";

/* Frosted-glass pill used for SALE / FEATURED / custom badges */
export function GlassBadge({ tone = "green", children, testid }) {
  const tones = {
    red: "bg-[#991B1B]/65 text-white border-white/35",
    green: "bg-[#0E4D34]/65 text-white border-white/35",
    gold: "bg-[#D4AF37]/70 text-neutral-900 border-white/45",
  };
  return (
    <span data-testid={testid}
          className={`inline-flex items-center rounded-full border backdrop-blur-md backdrop-saturate-150 shadow-[0_4px_14px_rgba(0,0,0,0.12)] px-2.5 py-1 text-[9px] sm:text-[10px] font-semibold tracking-[0.18em] uppercase ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function WishlistButton({ product, className = "", size = 20 }) {
  const { user, wishlist, toggleWishlist } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const saved = wishlist?.has(product.id);
  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast("Sign in to save pieces to your wishlist");
      nav(`/login?redirect=${encodeURIComponent(loc.pathname + loc.search)}`);
      return;
    }
    await toggleWishlist(product);
  };
  return (
    <button type="button" onClick={onClick} aria-label={saved ? "Remove from wishlist" : "Add to wishlist"} aria-pressed={!!saved}
            data-testid={`wishlist-${product.slug}`}
            className={`p-1.5 transition-transform hover:scale-110 active:scale-90 ${className}`}>
      {/* heart only — no circle; a soft shadow keeps it visible on light and dark photos */}
      <Heart size={size} strokeWidth={2}
             className={saved ? "text-[#c0262d]" : "text-white"}
             fill={saved ? "#c0262d" : "rgba(0,0,0,0.12)"}
             style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,.55))" }}/>
    </button>
  );
}

export function ProductCard({ product, index = 0 }) {
  const img = (product.images || [])[0];
  const sale = isOnSale(product);
  const showCompare = sale && product.compare_at_price && Number(product.compare_at_price) > Number(product.price);
  const discount = showCompare ? discountPct(product) : 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: Math.min(index, 8) * 0.05 }}
      // The card uses 3 rows of its parent grid (photo · name · price) through CSS subgrid, so in every row of cards
      // the names line up and the prices line up — with no empty gap when all names in that row are short.
      className="product-card group grid grid-rows-subgrid row-span-3 gap-y-0"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="grid grid-rows-subgrid row-span-3 gap-y-0">
        <div className="image-hover-zoom relative aspect-[3/4] bg-neutral-100 mb-3 sm:mb-4 overflow-hidden rounded-2xl">
          {img ? (
            <>
              {/* full photo (no cropping); a blurred copy fills any empty sides */}
              <img src={fileUrl(img)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-50" loading="lazy"/>
              <img src={fileUrl(img)} alt={product.name} className="relative w-full h-full object-contain" loading="lazy"/>
            </>
          ) : <div className="w-full h-full bg-neutral-200"/>}

          {(sale || product.featured || product.badge) && (
            <div className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3 flex flex-col items-start gap-1.5 pr-12">
              {sale && <GlassBadge tone="red" testid="badge-sale">{discount > 0 ? `${discount}% OFF` : "Sale"}</GlassBadge>}
              {product.featured && <GlassBadge tone="green" testid="badge-featured">Featured</GlassBadge>}
              {product.badge && <GlassBadge tone="gold" testid="badge-custom">{product.badge}</GlassBadge>}
            </div>
          )}

          <WishlistButton product={product} className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3"/>

          {product.stock === 0 && (
            <div className="absolute bottom-3 left-3 right-3 text-center rounded-full bg-white/80 backdrop-blur text-[10px] uppercase tracking-widest py-1.5">Sold out</div>
          )}
        </div>
        {/* row 2: fabric (if any) + name — at most 2 lines */}
        <div className="px-0.5">
          {product.fabric && <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 truncate mb-0.5">{product.fabric}</div>}
          <h3 className="font-serif text-base sm:text-lg text-neutral-900 !leading-[1.35] line-clamp-2 pb-px" title={product.name}>{product.name}</h3>
        </div>
        {/* row 3: price right under the name, then the rating (if any) */}
        <div className="px-0.5 pt-1.5 sm:pt-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[#0E4D34] font-semibold">{inr(product.price)}</span>
            {showCompare && (
              <>
                <span className="text-neutral-400 line-through text-sm" data-testid="card-mrp">{inr(product.compare_at_price)}</span>
                <span className="text-[#991B1B] text-xs font-semibold">({discount}% off)</span>
              </>
            )}
          </div>
          {product.rating_count > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-neutral-500 mt-1" data-testid="card-rating"><Stars value={product.rating_avg} size={12}/><span>{Number(product.rating_avg).toFixed(1)} ({product.rating_count})</span></div>
          )}
        </div>
      </Link>
    </motion.div>
  );
}

export function SkeletonCard() {
  return (
    <div className="animate-pulse">
      <div className="aspect-[3/4] bg-neutral-200 mb-4 rounded-2xl"/>
      <div className="h-3 bg-neutral-200 w-1/3 mb-2 rounded"/>
      <div className="h-5 bg-neutral-200 w-2/3 mb-2 rounded"/>
      <div className="h-4 bg-neutral-200 w-1/4 rounded"/>
    </div>
  );
}
